import { z } from 'zod';

/**
 * Transform the result of a zod scheme to another type and validates that type against another zod scheme.
 * The result of this function basically equals inputSchema.transform((val) => outputSchema.parse(transformer(val))),
 * but errors thrown during the transformation are handled gracefully (since at the moment, zod transformers
 * do not natively support exceptions).
 */
export function transformValidator<Output, Input1, Input2, Input3>(
  inputSchema: z.ZodType<Input2, any, Input1>,
  transformer: (input: Input2) => Input3,
  outputSchema: z.ZodType<Output, any, Input3>
): z.ZodEffects<z.ZodEffects<z.ZodType<Input2, any, Input1>, Input2>, Output> {
  // For now we have to parse the schema twice, since transform() is not allowed to throw exceptions.
  // See https://github.com/colinhacks/zod/pull/420
  return inputSchema
    .superRefine((val, ctx) => {
      try {
        const result = outputSchema.safeParse(transformer(val));
        if (!result.success) {
          for (const issue of result.error.errors) {
            ctx.addIssue(issue);
          }
        }
      } catch (err: any) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: err.message
        });
      }
    })
    .transform((val) => outputSchema.parse(transformer(val)));
}

/**
 * Returns a zod schema that takes a JSON string as an input and creates the parsed JSON object as output, validating it
 * against the specified schema. undefined or an empty string will be interpreted as undefined (leading to an error
 * if the schema does not allow undefined as a value). Any other invalid JSON strings or non-strings will lead to an
 * error.
 */
export function jsonStringValidator<Output>(
  schema: z.ZodType<Output, any, any>
): z.ZodEffects<
  z.ZodEffects<
    z.ZodType<string | undefined, any, string | undefined>,
    string | undefined
  >,
  Output
> {
  return transformValidator(
    z.string().optional(),
    (val) => (val ? JSON.parse(val) : undefined),
    schema
  );
}

/**
 * Returns a zod schema that takes a boolean string ("true" or "false") as an input and creates the parsed boolean value
 * as an output.
 * @param schema A schema can be specified here to parse the transformed boolean value (true/false/undefined).
 *               Examples would be z.boolean() or z.boolean().optional().
 */
export function booleanStringValidator<Output>(
  schema: z.ZodType<Output, any, boolean | undefined>
): z.ZodEffects<
  z.ZodEffects<
    z.ZodType<'' | 'false' | 'true' | undefined, any, '' | 'false' | 'true' | undefined>,
    '' | 'false' | 'true' | undefined
  >,
  Output
> {
  return transformValidator(
    z.enum(['', 'false', 'true']).optional(),
    (val) => (val === 'true' ? true : val === 'false' ? false : undefined),
    schema
  );
}
